import { useState, FormEvent } from "react";

interface BookingModalProps {
  open: boolean;
  onClose: () => void;
}

type FormStatus = "idle" | "loading" | "success" | "error";

export default function BookingModal({ open, onClose }: BookingModalProps) {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");

  if (!open) return null;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const fd = new FormData(formEl);

    // honeypot check
    const honeypot = (fd.get("website_alt") as string | null)?.trim() ?? "";
    if (honeypot) {
      setStatus("success");
      formEl.reset();
      return;
    }
    fd.delete("website_alt");

    // char limit for message
    const message = (fd.get("message") as string | null) ?? "";
    if (message.length > 500) {
      setStatus("error");
      setErrorMsg("留言不能超过 500 字");
      return;
    }

    setStatus("loading");
    setErrorMsg("");

    try {
      const body = new URLSearchParams();
      fd.forEach((value, key) => {
        body.append(key, value.toString());
      });

      const response = await fetch("https://readdy.ai/api/form/d9eg9r8o4b7spa5ij080", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      const serverMsg = parsed?.meta?.message ?? parsed?.meta?.detail ?? "";
      const isSpam = /spam/i.test(serverMsg) || /spam/i.test(responseText);

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        formEl.reset();
      } else {
        setStatus("error");
        setErrorMsg(serverMsg || "预约提交失败，请稍后重试");
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg("网络异常，请检查连接后重试");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-espresso-900/80 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      <div className="relative w-full max-w-md bg-cream-50 rounded-lg overflow-hidden border border-gold-400/40 max-h-[92vh] overflow-y-auto">
        <div className="relative px-6 pt-6 pb-4 bg-espresso-900 text-cream-50">
          <button
            onClick={onClose}
            aria-label="关闭"
            className="cursor-pointer absolute top-3 right-3 w-9 h-9 rounded-full bg-cream-50/10 hover:bg-cream-50/20 flex items-center justify-center transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
          <div className="inline-flex items-center gap-2 text-gold-300 text-[11px] font-serif tracking-[0.3em] uppercase">
            <span className="h-px w-6 bg-gold-400"></span>
            Reservation
          </div>
          <h3 className="mt-2 font-display text-2xl">预约免费试抽</h3>
          <p className="mt-1 text-xs font-sc text-cream-100/70">
            填写信息后，侍茄顾问将在 30 分钟内与您确认到店时间
          </p>
        </div>

        {status === "success" ? (
          <div className="px-6 py-10 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-gold-400/15 border border-gold-400/40 flex items-center justify-center">
              <i className="ri-checkbox-circle-line text-2xl text-gold-500"></i>
            </div>
            <h4 className="mt-4 font-display text-xl text-espresso-900">预约成功</h4>
            <p className="mt-2 text-sm font-sc text-espresso-700/70">
              我们已收到您的预约信息，稍后专属顾问将通过微信与您联系。
            </p>
            <button
              onClick={onClose}
              className="cursor-pointer whitespace-nowrap mt-6 inline-flex items-center gap-2 rounded-md bg-espresso-900 hover:bg-espresso-800 transition-colors text-cream-50 font-sc px-6 py-2.5 text-sm"
            >
              好的，返回页面
            </button>
          </div>
        ) : (
          <form
            data-readdy-form
            id="cigar-booking-form"
            onSubmit={handleSubmit}
            className="px-6 py-5 space-y-4"
          >
            {/* honeypot */}
            <input
              type="text"
              name="website_alt"
              className="field-optional-alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <div>
              <label className="block font-sc text-xs text-espresso-700 mb-1.5">姓名 / 尊称</label>
              <input
                required
                type="text"
                name="name"
                placeholder="请输入您的称呼"
                className="w-full text-sm font-sc rounded-md border border-cream-200 bg-white px-3.5 py-2.5 focus:outline-none focus:border-gold-400 transition-colors"
              />
            </div>

            <div>
              <label className="block font-sc text-xs text-espresso-700 mb-1.5">手机号码</label>
              <input
                required
                type="tel"
                name="phone"
                pattern="^1[3-9]\d{9}$"
                placeholder="请输入 11 位手机号"
                className="w-full text-sm font-sc rounded-md border border-cream-200 bg-white px-3.5 py-2.5 focus:outline-none focus:border-gold-400 transition-colors"
              />
            </div>

            <div>
              <label className="block font-sc text-xs text-espresso-700 mb-1.5">意向门店</label>
              <select
                required
                name="store"
                defaultValue=""
                className="w-full text-sm font-sc rounded-md border border-cream-200 bg-white px-3.5 py-2.5 focus:outline-none focus:border-gold-400 transition-colors pr-8"
              >
                <option value="" disabled>请选择意向门店</option>
                <option value="上海外滩源店">上海 · 外滩源旗舰店</option>
                <option value="北京国贸店">北京 · 国贸私享店</option>
                <option value="成都春熙店">成都 · 春熙路店</option>
                <option value="深圳前海店">深圳 · 前海店</option>
              </select>
            </div>

            <div>
              <label className="block font-sc text-xs text-espresso-700 mb-1.5">口感偏好</label>
              <select
                name="preference"
                defaultValue="均衡型"
                className="w-full text-sm font-sc rounded-md border border-cream-200 bg-white px-3.5 py-2.5 focus:outline-none focus:border-gold-400 transition-colors pr-8"
              >
                <option value="清淡型">清淡型 · 花草香为主</option>
                <option value="均衡型">均衡型 · 木香带果甜</option>
                <option value="浓郁型">浓郁型 · 皮革与烟熏</option>
                <option value="不确定">首次尝试 · 让顾问推荐</option>
              </select>
            </div>

            <div>
              <label className="block font-sc text-xs text-espresso-700 mb-1.5">
                备注 <span className="text-espresso-700/50">（可选，≤500 字）</span>
              </label>
              <textarea
                name="message"
                rows={2}
                maxLength={500}
                placeholder="是否需要预约红酒搭配 / 到店时间偏好"
                className="w-full text-sm font-sc rounded-md border border-cream-200 bg-white px-3.5 py-2.5 focus:outline-none focus:border-gold-400 transition-colors resize-none"
              />
            </div>

            {status === "error" && errorMsg && (
              <div className="rounded-md bg-wine-500/10 border border-wine-500/30 px-3 py-2 text-xs font-sc text-wine-600 flex items-start gap-2">
                <i className="ri-error-warning-line mt-0.5"></i>
                <span>{errorMsg}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={status === "loading"}
              className="cursor-pointer whitespace-nowrap w-full inline-flex items-center justify-center gap-2 rounded-md bg-espresso-900 hover:bg-espresso-800 disabled:opacity-70 transition-colors text-gold-300 font-sc font-medium px-6 py-3 text-sm"
            >
              {status === "loading" ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  正在提交...
                </>
              ) : (
                <>
                  <i className="ri-vip-crown-2-line"></i>
                  确认预约 · 尊享席位
                </>
              )}
            </button>

            <p className="text-[10px] text-espresso-700/50 font-sc text-center leading-relaxed">
              仅限 22 周岁以上成年人 · 提交视为同意《隐私政策》<br />
              我们承诺信息仅用于本次预约联系，不做他用。
            </p>
          </form>
        )}
      </div>
    </div>
  );
}